/**
 * Factory functions for FilterEditor section descriptors.
 * Each factory validates required fields and returns a frozen descriptor.
 */

/**
 * @typedef {{ value: string, label: string }} SelectOption
 */

/**
 * @typedef {Object} SortSectionConfig
 * @property {string} fieldKey - Dot-path key for the sort field (e.g. 'sort.field')
 * @property {string} ascendingKey - Dot-path key for the ascending flag (e.g. 'sort.ascending')
 * @property {SelectOption[]} options - Sort field options
 * @property {(field: string, ascending: boolean) => string|null} directionLabel
 */

/**
 * @param {SortSectionConfig} config
 */
export function sortSection({ fieldKey, ascendingKey, options, directionLabel }) {
  if (!fieldKey) throw new Error('sortSection: fieldKey is required');
  if (!ascendingKey) throw new Error('sortSection: ascendingKey is required');
  if (!options?.length) throw new Error('sortSection: options is required');
  if (typeof directionLabel !== 'function') throw new Error('sortSection: directionLabel must be a function');
  return Object.freeze({ type: 'sort', fieldKey, ascendingKey, options, directionLabel });
}

/**
 * @typedef {Object} SwitchSectionConfig
 * @property {string} key - Dot-path key for the boolean value
 * @property {string} label
 * @property {((filters: any) => boolean)} [visible]
 */

/**
 * @param {SwitchSectionConfig} config
 */
export function switchSection({ key, label, visible }) {
  if (!key) throw new Error('switchSection: key is required');
  if (!label) throw new Error('switchSection: label is required');
  return Object.freeze({ type: 'switch', key, label, ...(visible && { visible }) });
}

/**
 * @typedef {Object} SegmentedSectionConfig
 * @property {string} key - Dot-path key for the selected value
 * @property {string} label
 * @property {SelectOption[]} options
 * @property {((filters: any) => boolean)} [visible]
 */

/**
 * @param {SegmentedSectionConfig} config
 */
export function segmentedSection({ key, label, options, visible }) {
  if (!key) throw new Error('segmentedSection: key is required');
  if (!label) throw new Error('segmentedSection: label is required');
  if (!options?.length) throw new Error('segmentedSection: options is required');
  return Object.freeze({ type: 'segmented', key, label, options, ...(visible && { visible }) });
}

/**
 * @typedef {Object} SelectSectionConfig
 * @property {string} key - Dot-path key for the selected value
 * @property {string} label
 * @property {string} [placeholder]
 * @property {SelectOption[] | ((filters: any) => SelectOption[])} options
 * @property {((filters: any) => boolean)} [visible]
 */

/**
 * @param {SelectSectionConfig} config
 */
export function selectSection({ key, label, placeholder, options, visible }) {
  if (!key) throw new Error('selectSection: key is required');
  if (!label) throw new Error('selectSection: label is required');
  if (!options) throw new Error('selectSection: options is required');
  return Object.freeze({ type: 'select', key, label, placeholder, options, ...(visible && { visible }) });
}

/**
 * @typedef {Object} ChipsSectionConfig
 * @property {string} key
 * @property {string} label
 * @property {SelectOption[] | ((filters: any) => SelectOption[])} options
 * @property {((filters: any) => boolean)} [visible]
 */

/**
 * @param {ChipsSectionConfig} config
 */
export function chipsSection({ key, label, options, visible }) {
  if (!key) throw new Error('chipsSection: key is required');
  if (!label) throw new Error('chipsSection: label is required');
  if (!options) throw new Error('chipsSection: options is required');
  if (typeof options !== 'function' && !(options instanceof Array)) throw new Error('chipsSection: options must be a function or array');

  return Object.freeze({ type: 'chips', key, label, options, ...(visible && { visible }) });
}

/**
 * @typedef {Object} CheckboxGroupSectionConfig
 * @property {((filters: any) => Array<{ key: string, label: string, options: string[] }>)} items
 * @property {((filters: any) => boolean)} [visible]
 */

/**
 * @param {CheckboxGroupSectionConfig} config
 */
export function checkboxGroupSection({ items, visible }) {
  if (typeof items !== 'function') throw new Error('checkboxGroupSection: items must be a function');
  return Object.freeze({ type: 'checkboxGroup', items, ...(visible && { visible }) });
}
